/**
 * Shared website-format rule for the lead form (review P1-2).
 *
 * Accepts either a bare domain ("example.com") or a full http(s) URL, with
 * an optional port and/or path. A bare domain is the natural way to fill in
 * an optional "Website" field — the placeholder shows the scheme but
 * doesn't enforce it — so this is deliberately permissive about the scheme
 * while still rejecting genuine garbage ("not a url").
 *
 * Used by BOTH:
 *   - the client (lead-capture-form.tsx) — rejects a non-matching value
 *     with a field error, before the user can even submit;
 *   - the server (server/api/routers/leads.ts) — rejects the same way, and
 *     additionally normalizes an accepted bare domain to a full https://
 *     URL before it's stored.
 *
 * A single shared module (rather than two copies of the same regex literal)
 * means the two can never quietly drift apart — which is exactly how P1-2
 * happened: the client accepted anything, the server required a scheme, and
 * a mismatch there silently dropped the ENTIRE lead submission (see that
 * router file's comment for the full story).
 */

export const WEBSITE_RE =
  /^(https?:\/\/)?([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i;

export function isValidWebsiteInput(value: string): boolean {
  return WEBSITE_RE.test(value);
}

/** Assumes `isValidWebsiteInput(value)` is already true — prepends
 * `https://` only when no scheme is present. */
export function normalizeWebsite(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}
